'use client';

/**
 * Sets drawer extra content based on pathname.
 * - On /settings: Profile / Edit Profile / Analytics / Security / Activity / Privacy.
 * - On /admin: Admin home + Settings sections (System, Users, Portal, etc.) so mobile
 *   uses the top bar hamburger only (no floating admin button).
 */

import { ModuleIcon } from '@korvo/core/components/shared/Icon/ModuleIcon';
import { DrawerExtraContext } from '@korvo/core/components/shared/TopBar/DrawerExtraContext';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useContext, useEffect, useMemo, useState } from 'react';

const SETTINGS_TABS = [
  { id: 'profile', label: 'Profile', icon: 'ri-user-line' },
  { id: 'edit', label: 'Edit Profile', icon: 'ri-edit-line' },
  { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-line' },
  { id: 'security', label: 'Security', icon: 'ri-shield-keyhole-line' },
  { id: 'activity', label: 'Activity', icon: 'ri-time-line' },
  { id: 'privacy', label: 'Privacy', icon: 'ri-lock-line' },
] as const;

/** Matches AdminSettingsClientLayout hierarchy (labels + icons only, no descriptions). */
const ADMIN_HOME = { href: '/admin', label: 'Admin home', icon: 'ri-home-4-line' } as const;

const ADMIN_SECTIONS: {
  id: string;
  label: string;
  items: { id: string; label: string; icon: string; href: string }[];
}[] = [
  {
    id: 'core',
    label: 'Core',
    items: [
      { id: 'system', label: 'System', icon: 'ri-settings-3-line', href: '/admin/settings/system' },
      { id: 'users', label: 'Users', icon: 'ri-user-line', href: '/admin/settings/users' },
      { id: 'registration-codes', label: 'Registration Codes', icon: 'ri-key-line', href: '/admin/settings/registration-codes' },
      { id: 'homepage', label: 'Homepage', icon: 'ri-home-4-line', href: '/admin/settings/homepage' },
    ],
  },
  {
    id: 'ai',
    label: 'AI & Modules',
    items: [
      { id: 'portal', label: 'Portal', icon: 'ri-cpu-line', href: '/admin/settings/portal' },
      { id: 'configs', label: 'Prompt Configs', icon: 'ri-sliders-line', href: '/admin/settings/configs' },
      { id: 'modules', label: 'Modules', icon: 'ri-brain-line', href: '/admin/settings/modules' },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    items: [
      { id: 'audit', label: 'Audit Log', icon: 'ri-file-text-line', href: '/admin/settings/audit' },
      { id: 'transcripts', label: 'Transcripts', icon: 'ri-file-text-line', href: '/admin/settings/transcripts' },
    ],
  },
];

function SettingsNavLinks({ onClose }: { onClose: () => void }) {
  const [hash, setHash] = useState('');
  useEffect(() => {
    const read = () => setHash(typeof window !== 'undefined' ? window.location.hash.slice(1) || 'profile' : 'profile');
    read();
    window.addEventListener('hashchange', read);
    return () => window.removeEventListener('hashchange', read);
  }, []);

  return (
    <div className="space-y-1" data-testid="settings-drawer-nav">
      <div className="text-xs font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: 'var(--foreground-muted)' }}>
        Settings
      </div>
      {SETTINGS_TABS.map((tab) => {
        const isActive = (hash || 'profile') === tab.id;
        return (
          <Link
            key={tab.id}
            href={`/settings#${tab.id}`}
            onClick={onClose}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium transition-colors"
            style={{
              color: isActive ? 'var(--primary-default)' : 'var(--foreground-default)',
              backgroundColor: isActive ? 'var(--panel-hover)' : 'transparent',
            }}
            data-testid={`settings-drawer-${tab.id}`}
          >
            <ModuleIcon icon={tab.icon} className="h-4 w-4" style={{ color: 'inherit' }} />
            <span>{tab.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

function AdminNavLinks({ onClose, pathname }: { onClose: () => void; pathname: string }) {
  const sectionHeaderClass = 'text-xs font-semibold uppercase tracking-wider px-2 py-1.5 mt-3 first:mt-0';
  const sectionHeaderStyle = { color: 'var(--foreground-muted)' };
  const linkClass = 'flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium transition-colors';
  const linkStyle = (isActive: boolean) => ({
    color: isActive ? 'var(--primary-default)' : 'var(--foreground-default)',
    backgroundColor: isActive ? 'var(--panel-hover)' : 'transparent',
  });

  return (
    <div className="overflow-y-auto max-h-[min(70vh,28rem)] -mx-1 px-1" data-testid="admin-drawer-nav">
      <div className={sectionHeaderClass} style={sectionHeaderStyle}>
        Admin
      </div>
      <Link
        href={ADMIN_HOME.href}
        onClick={onClose}
        className={linkClass}
        style={linkStyle(pathname === ADMIN_HOME.href)}
        data-testid="admin-drawer-admin"
      >
        <ModuleIcon icon={ADMIN_HOME.icon} className="h-4 w-4 shrink-0" style={{ color: 'inherit' }} />
        <span>{ADMIN_HOME.label}</span>
      </Link>
      {ADMIN_SECTIONS.map((section) => (
        <div key={section.id} className="mt-1">
          <div className={sectionHeaderClass} style={sectionHeaderStyle}>
            {section.label}
          </div>
          <div className="space-y-0.5">
            {section.items.map((item) => {
              const isActive =
                pathname === item.href ||
                (item.href !== '/admin' && pathname.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onClose}
                  className={linkClass}
                  style={linkStyle(isActive)}
                  data-testid={`admin-drawer-${item.href.replace(/\//g, '-').replace(/^-/, '')}`}
                >
                  <ModuleIcon icon={item.icon} className="h-4 w-4 shrink-0" style={{ color: 'inherit' }} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Renders nothing; sets drawer extra content when pathname is /settings or /admin. */
export function PathAwareDrawerContent() {
  const pathname = usePathname();
  const { setExtraContent, closeDrawer } = useContext(DrawerExtraContext);

  const onSettings = pathname?.startsWith('/settings') ?? false;
  const onAdmin = pathname?.startsWith('/admin') ?? false;

  const content = useMemo(() => {
    if (onSettings) return <SettingsNavLinks onClose={closeDrawer} />;
    if (onAdmin && pathname) return <AdminNavLinks onClose={closeDrawer} pathname={pathname} />;
    return null;
  }, [onSettings, onAdmin, pathname, closeDrawer]);

  useEffect(() => {
    setExtraContent(content);
    return () => setExtraContent(null);
  }, [content, setExtraContent]);

  return null;
}
